import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage';

/**
 * A bid in the last minute pushes the end date back by a minute, and that can
 * happen repeatedly. Outliving the auction by a few minutes keeps a dismissal
 * in place through a bidding war.
 */
const EXTENSION_BUFFER_MS = 10 * 60 * 1000;

type IgnoredBid = { id: string; endDate: string };

const stillHidden = (entry: IgnoredBid) =>
  new Date(entry.endDate).getTime() + EXTENSION_BUFFER_MS > Date.now();

/**
 * Bids the user dismissed from the banner, kept per browser. Entries are
 * dropped once their auction has ended, so the list stays bounded by the
 * auctions the user is bidding on rather than growing forever.
 */
export const useIgnoredBids = () => {
  const [stored, setStored] = useLocalStorage('myBidsBannerIgnoredBids', []);

  // Legacy entries are bare ids, written before dismissals had an expiry.
  const hidden = stored.filter(
    (entry): entry is IgnoredBid =>
      typeof entry !== 'string' && stillHidden(entry)
  );

  return {
    ignoredBids: hidden.map(({ id }) => id),
    // Writing back the filtered list is what keeps the stored array bounded.
    ignoreBid: ({ id, endDate }: { id: string; endDate: Date }) =>
      setStored([...hidden, { id, endDate: endDate.toISOString() }]),
  };
};
